import { Component, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { BuildingService } from '@artemis-software/wr-api';
import { FileUploadService } from '@services/file-upload.service';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';

const TOAST_TITLE = 'HORA-Pass';

@Component({
  standalone: false,
  selector: 'wr-building-hora-pass',
  templateUrl: './building-hora-pass.component.html',
  styleUrl: './building-hora-pass.component.scss',
})
export class BuildingHoraPassComponent {
  readonly buildingId = input.required<string>();
  readonly canEdit = input(false);

  private readonly buildingService = inject(BuildingService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  readonly loading = loader();
  readonly saving = signal(false);

  readonly horaPass = asyncComputed(() => this.buildingService.findHoraPass(this.buildingId()), undefined, this.loading);

  async onFileSelected(event: Event): Promise<void> {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) {
      return;
    }
    await this.runSaving('HORA-Pass gespeichert', async () => {
      const uploaded = await firstValueFrom(this.fileUploadService.uploadSingleFileNoProgress(file));
      await firstValueFrom(this.buildingService.updateHoraPass(this.buildingId(), {
        s3Key: uploaded.key,
        rotationDegrees: 0,
        comment: '',
        tags: [],
        deleted: false,
      }));
    });
  }

  deleteHoraPass(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'HORA-Pass löschen?',
        message: 'Der HORA-Pass wird bei künftig generierten Dokumenten nicht mehr angehängt.',
      },
    }).onClose.subscribe(async (confirmed) => {
      if (!confirmed) {
        return;
      }
      await this.runSaving('HORA-Pass gelöscht', async () => {
        await firstValueFrom(this.buildingService.deleteHoraPass(this.buildingId()));
      });
    });
  }

  private async runSaving(successMessage: string, action: () => Promise<void>): Promise<void> {
    this.saving.set(true);
    try {
      await action();
      this.toastrService.success(successMessage, TOAST_TITLE);
      this.horaPass.reload();
    } catch {
      /* no-op */
    } finally {
      this.saving.set(false);
    }
  }
}
